/**
 * Blossom — global.js
 * Loaded (deferred) on every page. Provides the `Theme` namespace:
 * event bus, fetch helpers, section rendering, scroll lock, a11y announcer,
 * toasts, safe storage, lazy script loading, the <dialog> base class used by
 * drawers/modals, and small shared custom elements.
 *
 * Custom events (dispatched on `document`):
 *   theme:cart:updated          { cart, source }
 *   theme:cart:error            { message, source }
 *   theme:product:variant-change{ variant, sectionId, productId }
 *   theme:drawer:open / close   { id }
 *   theme:modal:open / close    { id }
 */
(() => {
  if (window.Theme && window.Theme.ready) return;

  const Theme = (window.Theme = window.Theme || {});
  Theme.ready = true;
  Theme.routes = window.routes || {};
  Theme.strings = window.themeStrings || {};
  Theme.settings = window.themeSettings || {};
  Theme.designMode = Boolean(window.Shopify && window.Shopify.designMode);

  Theme.events = Object.freeze({
    CART_UPDATED: 'theme:cart:updated',
    CART_ERROR: 'theme:cart:error',
    CART_OPEN: 'theme:cart:open',
    VARIANT_CHANGE: 'theme:product:variant-change',
    DRAWER_OPEN: 'theme:drawer:open',
    DRAWER_CLOSE: 'theme:drawer:close',
    MODAL_OPEN: 'theme:modal:open',
    MODAL_CLOSE: 'theme:modal:close',
    QUICK_ADD_OPEN: 'theme:quick-add:open',
    WISHLIST_CHANGE: 'theme:wishlist:change',
    QUIZ_COMPLETE: 'theme:quiz:complete',
  });

  /* ---------------------------------------------------------- Events */
  Theme.emit = (name, detail = {}, target = document) => {
    target.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
  };

  Theme.on = (name, handler, target = document) => {
    target.addEventListener(name, handler);
    return () => target.removeEventListener(name, handler);
  };

  /* --------------------------------------------------------- Helpers */
  Theme.debounce = (fn, wait = 250) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  Theme.prefersReducedMotion = () =>
    document.documentElement.dataset.reducedMotion === 'true' ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  Theme.isMobile = () => window.matchMedia('(max-width: 749px)').matches;

  Theme.parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /** Wait for a CSS transition on `element` (or a timeout fallback). */
  Theme.afterTransition = (element, fallback = 600) =>
    new Promise((resolve) => {
      if (Theme.prefersReducedMotion()) return resolve();
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        element.removeEventListener('transitionend', onEnd);
        resolve();
      };
      const onEnd = (event) => {
        if (event.target === element) finish();
      };
      element.addEventListener('transitionend', onEnd);
      setTimeout(finish, fallback);
    });

  /* ----------------------------------------------------------- Fetch */
  class ThemeError extends Error {
    constructor(message, { status, data } = {}) {
      super(message);
      this.name = 'ThemeError';
      this.status = status;
      this.data = data;
    }
  }
  Theme.Error = ThemeError;

  /**
   * fetch() + JSON with consistent errors. Shopify Ajax API errors come back as
   * { status, message, description } — `description` is customer friendly.
   */
  Theme.fetchJSON = async (url, options = {}) => {
    const init = {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...(options.body && !(options.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {}),
      },
    };
    let response;
    try {
      response = await fetch(url, init);
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      throw new ThemeError(Theme.strings.networkError || 'Network error. Please check your connection and try again.');
    }
    let data = null;
    try {
      data = await response.json();
    } catch (error) {
      data = null;
    }
    if (!response.ok || (data && data.status && Number(data.status) >= 400)) {
      const message =
        (data && (data.description || data.message || data.errors)) ||
        Theme.strings.genericError ||
        'Something went wrong. Please try again.';
      throw new ThemeError(typeof message === 'string' ? message : JSON.stringify(message), {
        status: response.status,
        data,
      });
    }
    return data;
  };

  /**
   * Section Rendering API: fetch one section's HTML for a URL.
   * Returns the parsed Document so callers can query what they need.
   */
  Theme.fetchSection = async (url, sectionId, { signal } = {}) => {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    let response;
    try {
      response = await fetch(target.toString(), { signal, credentials: 'same-origin' });
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      throw new ThemeError(Theme.strings.networkError || 'Network error. Please try again.');
    }
    if (!response.ok) {
      throw new ThemeError(Theme.strings.genericError || 'Something went wrong. Please try again.', {
        status: response.status,
      });
    }
    return Theme.parseHTML(await response.text());
  };

  /** Replace the inner HTML of `selector` in the page with the same selector from `doc`. */
  Theme.replaceFromDocument = (doc, selector, root = document) => {
    const source = doc.querySelector(selector);
    const target = root.querySelector(selector);
    if (source && target) target.innerHTML = source.innerHTML;
    return Boolean(source && target);
  };

  /* ----------------------------------------------------- Script loader */
  const scriptPromises = new Map();
  Theme.loadScript = (src) => {
    if (!src) return Promise.reject(new Error('No script src'));
    if (scriptPromises.has(src)) return scriptPromises.get(src);
    const existing = document.querySelector(`script[src="${src}"]`);
    const promise = new Promise((resolve, reject) => {
      if (existing && existing.dataset.loaded === 'true') return resolve();
      if (existing && !existing.dataset.themeLoader) {
        // A tag from the HTML: deferred scripts have run by DOMContentLoaded.
        if (document.readyState !== 'loading') return resolve();
        document.addEventListener('DOMContentLoaded', () => resolve(), { once: true });
        return;
      }
      const script = existing || document.createElement('script');
      script.dataset.themeLoader = 'true';
      script.addEventListener('load', () => {
        script.dataset.loaded = 'true';
        resolve();
      });
      script.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)));
      if (!existing) {
        script.src = src;
        script.defer = true;
        document.head.appendChild(script);
      }
    });
    scriptPromises.set(src, promise);
    return promise;
  };

  /* ------------------------------------------------------- Scroll lock */
  let lockCount = 0;
  Theme.lockScroll = () => {
    lockCount += 1;
    document.documentElement.classList.add('scroll-lock');
  };
  Theme.unlockScroll = () => {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) document.documentElement.classList.remove('scroll-lock');
  };

  /* ------------------------------------------------ Focus management */
  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable="true"], summary';
  Theme.getFocusable = (container) =>
    Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (el) => !el.closest('[inert]') && el.getClientRects().length > 0
    );

  /* ------------------------------------------------------- Announcer */
  Theme.announce = (message, politeness = 'polite') => {
    const region = document.getElementById(politeness === 'assertive' ? 'A11yAlert' : 'A11yStatus');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 60);
  };

  /* ------------------------------------------------------------ Toast */
  Theme.toast = (message, { type = 'info', timeout = 6000 } = {}) => {
    if (!message) return;
    let region = document.querySelector('.toast-region');
    if (!region) {
      region = document.createElement('div');
      region.className = 'toast-region';
      document.body.appendChild(region);
    }
    const toast = document.createElement('div');
    toast.className = `toast toast--${type}`;
    toast.setAttribute('role', type === 'error' ? 'alert' : 'status');
    const text = document.createElement('p');
    text.className = 'toast__message';
    text.textContent = message;
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'icon-button';
    close.setAttribute('aria-label', Theme.strings.close || 'Close');
    close.innerHTML =
      '<svg class="icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M5 5l10 10M15 5L5 15"/></svg>';
    close.addEventListener('click', () => toast.remove());
    toast.append(text, close);
    region.appendChild(toast);
    if (timeout) window.setTimeout(() => toast.remove(), timeout);
  };

  /* ---------------------------------------------------- Safe storage */
  Theme.storage = {
    get(key, fallback = null) {
      try {
        const value = window.localStorage.getItem(key);
        return value === null ? fallback : JSON.parse(value);
      } catch (error) {
        return fallback;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
        return true;
      } catch (error) {
        return false;
      }
    },
    remove(key) {
      try {
        window.localStorage.removeItem(key);
      } catch (error) {
        /* storage unavailable (private mode) — nothing to remove */
      }
    },
  };

  /* ------------------------------------------------------ Money format */
  /**
   * Fallback money formatter for client-only values. Prefer server-rendered
   * prices (Section Rendering API / pre-formatted JSON) which respect Markets.
   */
  Theme.formatMoney = (cents, format = Theme.settings.moneyFormat || '{{amount}}') => {
    const value = Number(cents) / 100;
    const formatNumber = (number, decimals, thousands = ',', decimal = '.') => {
      const [whole, fraction] = number.toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return formatNumber(value, 0);
        case 'amount_with_comma_separator':
          return formatNumber(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return formatNumber(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return formatNumber(value, 2, "'", '.');
        case 'amount_with_space_separator':
          return formatNumber(value, 2, ' ', ',');
        default:
          return formatNumber(value, 2);
      }
    });
  };

  /* ------------------------------------------------ Dialog base class */
  /**
   * Base for <drawer-component> and <modal-component>. Wraps a native
   * <dialog> (top layer, inert background, ESC handling) and adds animated
   * open/close, scroll lock, focus return and opener wiring.
   *
   *   <drawer-component id="CartDrawer"><dialog class="drawer">…</dialog></drawer-component>
   *   <button data-open="CartDrawer" aria-controls="CartDrawer" aria-expanded="false">
   */
  class ThemeDialog extends HTMLElement {
    static openEvent = 'theme:dialog:open';
    static closeEvent = 'theme:dialog:close';

    connectedCallback() {
      this.dialog = this.querySelector(':scope > dialog');
      if (!this.dialog || this.dataset.initialized) return;
      this.dataset.initialized = 'true';

      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target.closest('[data-close]') || event.target.matches('[data-overlay]')) {
          event.preventDefault();
          this.close();
        }
      });
      // Browsers may force-close a dialog (e.g. repeated ESC) without `cancel`.
      this.dialog.addEventListener('close', () => {
        if (!this.closing) this.cleanup();
      });
    }

    get isOpen() {
      return this.dialog && this.dialog.open;
    }

    open(opener) {
      if (!this.dialog || this.isOpen) return;
      this.opener = opener || document.activeElement;
      Theme.lockScroll();
      this.dialog.showModal();
      this.setOpenerState(true);
      requestAnimationFrame(() => {
        this.dialog.classList.add('is-open');
        this.focusInitial();
      });
      this.onOpen();
      Theme.emit(this.constructor.openEvent, { id: this.id }, this);
    }

    async close() {
      if (!this.isOpen || this.closing) return;
      this.closing = true;
      this.dialog.classList.remove('is-open');
      const panel = this.dialog.querySelector('[data-panel]') || this.dialog;
      await Theme.afterTransition(panel);
      this.dialog.close();
      this.closing = false;
      this.cleanup();
    }

    cleanup() {
      this.dialog.classList.remove('is-open');
      Theme.unlockScroll();
      this.setOpenerState(false);
      this.onClose();
      Theme.emit(this.constructor.closeEvent, { id: this.id }, this);
      if (this.opener && typeof this.opener.focus === 'function' && document.contains(this.opener)) {
        this.opener.focus({ preventScroll: true });
      }
    }

    toggle(opener) {
      if (this.isOpen) this.close();
      else this.open(opener);
    }

    focusInitial() {
      const target =
        this.dialog.querySelector('[autofocus]') ||
        this.dialog.querySelector('[data-initial-focus]') ||
        Theme.getFocusable(this.dialog.querySelector('[data-panel]') || this.dialog)[0];
      if (target) target.focus({ preventScroll: true });
    }

    setOpenerState(expanded) {
      document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((el) => {
        el.setAttribute('aria-expanded', String(expanded));
      });
    }

    /* Hooks for subclasses */
    onOpen() {}
    onClose() {}
  }
  Theme.Dialog = ThemeDialog;

  /** Any element with data-open="<dialog-component-id>" opens that component. */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open]');
    if (!opener) return;
    const target = document.getElementById(opener.dataset.open);
    if (!target || typeof target.open !== 'function') return;
    event.preventDefault();
    target.open(opener);
  });

  /* ----------------------------------------------- <details-dropdown> */
  /**
   * Enhances <details> dropdowns: closes on outside click, ESC and focus-out.
   * Used by localization selectors and header utility menus.
   */
  if (!customElements.get('details-dropdown')) {
    customElements.define(
      'details-dropdown',
      class DetailsDropdown extends HTMLElement {
        connectedCallback() {
          this.details = this.querySelector('details');
          this.summary = this.details && this.details.querySelector('summary');
          if (!this.details || !this.summary) return;
          this.summary.setAttribute('aria-expanded', this.details.open ? 'true' : 'false');
          this.details.addEventListener('toggle', () => {
            this.summary.setAttribute('aria-expanded', String(this.details.open));
          });
          this.addEventListener('keyup', (event) => {
            if (event.key === 'Escape' && this.details.open) {
              this.details.open = false;
              this.summary.focus();
            }
          });
          this.addEventListener('focusout', (event) => {
            if (!this.contains(event.relatedTarget)) this.details.open = false;
          });
          this.onDocumentClick = (event) => {
            if (!this.contains(event.target)) this.details.open = false;
          };
          document.addEventListener('click', this.onDocumentClick);
        }

        disconnectedCallback() {
          document.removeEventListener('click', this.onDocumentClick);
        }
      }
    );
  }

  /* ------------------------------------------------- <quantity-input> */
  if (!customElements.get('quantity-input')) {
    customElements.define(
      'quantity-input',
      class QuantityInput extends HTMLElement {
        connectedCallback() {
          this.input = this.querySelector('input');
          if (!this.input) return;
          this.addEventListener('click', (event) => {
            const button = event.target.closest('button[name]');
            if (!button) return;
            event.preventDefault();
            const previous = this.input.value;
            if (button.name === 'plus') this.input.stepUp();
            else this.input.stepDown();
            if (previous !== this.input.value) {
              this.input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
          this.input.addEventListener('change', () => this.updateButtons());
          this.updateButtons();
        }

        updateButtons() {
          const value = Number(this.input.value);
          const min = Number(this.input.min || 0);
          const max = this.input.max ? Number(this.input.max) : Infinity;
          const minus = this.querySelector('button[name="minus"]');
          const plus = this.querySelector('button[name="plus"]');
          if (minus) minus.disabled = value <= min;
          if (plus) plus.disabled = value >= max;
        }
      }
    );
  }

  /* ---------------------------------------------- <localization-form> */
  /** Submits Shopify's localization form when a country/language is chosen. */
  if (!customElements.get('localization-form')) {
    customElements.define(
      'localization-form',
      class LocalizationForm extends HTMLElement {
        connectedCallback() {
          this.form = this.querySelector('form');
          if (!this.form) return;
          this.addEventListener('click', (event) => {
            const option = event.target.closest('[data-value]');
            if (!option) return;
            event.preventDefault();
            const input = this.form.querySelector(`input[name="${option.dataset.name}"]`);
            if (!input) return;
            input.value = option.dataset.value;
            this.form.submit();
          });
          this.querySelectorAll('select').forEach((select) => {
            select.addEventListener('change', () => this.form.submit());
          });
        }
      }
    );
  }

  /* ------------------------------------------------ <accordion-group> */
  /** Optional: only one <details> open at a time inside the group. */
  if (!customElements.get('accordion-group')) {
    customElements.define(
      'accordion-group',
      class AccordionGroup extends HTMLElement {
        connectedCallback() {
          if (this.dataset.single !== 'true') return;
          this.addEventListener(
            'toggle',
            (event) => {
              if (!event.target.open) return;
              this.querySelectorAll('details[open]').forEach((details) => {
                if (details !== event.target) details.open = false;
              });
            },
            true
          );
        }
      }
    );
  }

  /* ---------------------------------------------------- Reveal on scroll */
  const initReveal = (root = document) => {
    const elements = root.querySelectorAll('.reveal:not(.is-revealed)');
    if (!('IntersectionObserver' in window) || Theme.prefersReducedMotion()) {
      elements.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    // Anything already on screen is shown immediately (no flash), the rest
    // animates in as it scrolls into view.
    elements.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) el.classList.add('is-revealed');
    });
    document.documentElement.classList.add('reveal-ready');
    if (!elements.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    elements.forEach((el) => observer.observe(el));
  };
  Theme.initReveal = initReveal;

  /* ----------------------------------------------- Wishlist placeholder */
  /**
   * Browser-only favorites. Apps can listen to theme:wishlist:change or
   * replace the button via an app block.
   */
  Theme.wishlist = {
    key: 'theme:wishlist',
    all() {
      return Theme.storage.get(this.key, []);
    },
    has(id) {
      return this.all().includes(String(id));
    },
    toggle(id) {
      const items = this.all();
      const key = String(id);
      const next = items.includes(key) ? items.filter((item) => item !== key) : [...items, key];
      Theme.storage.set(this.key, next);
      Theme.emit(Theme.events.WISHLIST_CHANGE, { id: key, active: next.includes(key), items: next });
      return next.includes(key);
    },
  };

  const syncWishlistButtons = (root = document) => {
    root.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      button.setAttribute('aria-pressed', String(Theme.wishlist.has(button.dataset.wishlistToggle)));
    });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;
    event.preventDefault();
    const active = Theme.wishlist.toggle(button.dataset.wishlistToggle);
    syncWishlistButtons();
    Theme.announce(active ? Theme.strings.wishlistAdded : Theme.strings.wishlistRemoved);
  });

  /* --------------------------------------- Product card swatch preview */
  /** Hovering/focusing a card swatch previews that variant's image. */
  const previewSwatch = (event) => {
    const swatch = event.target.closest?.('[data-swatch-image]');
    if (!swatch) return;
    const card = swatch.closest('[data-product-card]');
    const image = card?.querySelector('.product-card__image--primary');
    if (!image) return;
    if (!image.dataset.originalSrc) {
      image.dataset.originalSrc = image.currentSrc || image.src;
      image.dataset.originalSrcset = image.getAttribute('srcset') || '';
    }
    image.removeAttribute('srcset');
    image.src = swatch.dataset.swatchImage;
    card.querySelectorAll('.product-card__swatch.is-active').forEach((el) => el.classList.remove('is-active'));
    swatch.classList.add('is-active');
  };
  const resetSwatch = (event) => {
    const list = event.target.closest?.('.product-card__swatches');
    if (!list || list.contains(event.relatedTarget)) return;
    const card = list.closest('[data-product-card]');
    const image = card?.querySelector('.product-card__image--primary');
    if (!image || !image.dataset.originalSrc) return;
    image.src = image.dataset.originalSrc;
    if (image.dataset.originalSrcset) image.setAttribute('srcset', image.dataset.originalSrcset);
    card.querySelectorAll('.product-card__swatch.is-active').forEach((el) => el.classList.remove('is-active'));
  };
  document.addEventListener('pointerover', previewSwatch);
  document.addEventListener('focusin', previewSwatch);
  document.addEventListener('pointerout', resetSwatch);
  document.addEventListener('focusout', resetSwatch);

  /* --------------------------------------------- Button bloom origin */
  /**
   * With Theme settings › Buttons › Hover effect = Bloom, the fill (base.css,
   * .button::before) grows from where the pointer enters a button and drains
   * towards where it leaves.
   */
  const setBloomOrigin = (event) => {
    const button = event.target.closest?.('.button');
    if (!button || button.contains(event.relatedTarget)) return;
    const rect = button.getBoundingClientRect();
    button.style.setProperty('--bloom-x', `${Math.round(event.clientX - rect.left)}px`);
    button.style.setProperty('--bloom-y', `${Math.round(event.clientY - rect.top)}px`);
  };
  if (document.documentElement.dataset.buttonHover === 'bloom') {
    document.addEventListener('pointerover', setBloomOrigin);
    document.addEventListener('pointerout', setBloomOrigin);
  }

  /* ------------------------------------------------- Cart icon bump */
  /** The header bag gives a little hop whenever the item count goes up. */
  let knownItemCount = Number.parseInt(document.querySelector('[data-cart-count-bubble]')?.textContent, 10) || 0;
  Theme.on(Theme.events.CART_UPDATED, (event) => {
    const count = event.detail?.cart?.item_count;
    if (typeof count !== 'number') return;
    if (count > knownItemCount) {
      document.querySelectorAll('.header__cart').forEach((icon) => {
        icon.classList.remove('is-bumping');
        void icon.offsetWidth; // restart the animation
        icon.classList.add('is-bumping');
      });
    }
    knownItemCount = count;
  });

  /* ------------------------------------------------ Page loading line */
  /**
   * Theme settings › Splash screen › Loading line: shows .nav-progress
   * (motion.css) while the browser fetches the next page of the store.
   * Links whose click is handled in place (quick add, drawers, AJAX filters)
   * call preventDefault and are left alone.
   */
  if (document.querySelector('.nav-progress')) {
    document.addEventListener('click', (event) => {
      const link = event.target.closest?.('a[href]');
      if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if ((link.target && link.target !== '_self') || link.hasAttribute('download')) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || !/^https?:$/.test(url.protocol)) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      setTimeout(() => {
        if (!event.defaultPrevented) document.documentElement.classList.add('is-navigating');
      }, 0);
    });
    window.addEventListener('pageshow', () => document.documentElement.classList.remove('is-navigating'));
  }

  /* ---------------------------------------------------- Back to top */
  /** Round button with a scroll-progress ring (layout/theme.liquid, motion.css). */
  if (!customElements.get('back-to-top')) {
    customElements.define(
      'back-to-top',
      class BackToTop extends HTMLElement {
        connectedCallback() {
          this.hidden = false;
          this.querySelector('button')?.addEventListener('click', () => {
            window.scrollTo({ top: 0, behavior: Theme.prefersReducedMotion() ? 'auto' : 'smooth' });
            document.getElementById('MainContent')?.focus({ preventScroll: true });
          });
          this.onScroll = () => {
            if (this.frame) return;
            this.frame = requestAnimationFrame(() => {
              this.frame = null;
              const max = document.documentElement.scrollHeight - window.innerHeight;
              const progress = max > 0 ? Math.min(window.scrollY / max, 1) : 0;
              this.style.setProperty('--scroll-progress', progress.toFixed(3));
              this.classList.toggle('is-visible', window.scrollY > window.innerHeight * 1.2);
            });
          };
          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.onScroll();
        }

        disconnectedCallback() {
          window.removeEventListener('scroll', this.onScroll);
        }
      }
    );
  }

  /* ----------------------------------------------------------- Boot */
  const boot = () => {
    document.documentElement.classList.remove('no-js');
    document.documentElement.classList.add('js');
    initReveal();
    syncWishlistButtons();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* Theme editor: re-run lightweight initializers for injected sections */
  document.addEventListener('shopify:section:load', (event) => {
    initReveal(event.target);
    syncWishlistButtons(event.target);
  });
})();
